import {
  ChangeEventHandler,
  FC,
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
  useRef,
  useState,
} from 'react'
import cn from 'classnames'
import classNames from 'classnames'
import Icon, { IconName } from 'components/Icon'
import Strings from 'project/localisation'

function stripNonEuropeanCharacters(input) {
  return input
    .replace(/[^A-Za-zÀ-ÿŚś0-9\s.,;:!?'-£$€¥#@]/g, '')
    .replace(/\s+/g, ' ')
}
export type InputProps = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  textarea?: boolean
  isValid?: boolean
  placeholder?: string
  inputClassName?: string
  name?: string
  label?: string
  icon?: IconName
  iconLeft?: IconName
  paddingLeft?: number
  paddingRight?: number
  iconLeftPosition?: number
  id?: string
  type?: string
  textButton?: string
  className?: string
  errorMessage?: string
  iconColour?: string
  touched?: boolean
  value?: string
  onIconClick?: () => void
  actionButton?: ReactNode
  isVisibleActionButton?: boolean
  onActionButtonClick?: () => void
  onEnterPress?: () => void
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>
  disabled?: boolean
  'data-test'?: string
}

const Input: FC<InputProps> = ({
  actionButton,
  children,
  className,
  disabled,
  errorMessage,
  icon,
  iconColour,
  iconLeft,
  iconLeftPosition = 10,
  id,
  inputClassName,
  isValid = true,
  isVisibleActionButton = false,
  label,
  name,
  onActionButtonClick,
  onBlur,
  onEnterPress,
  onFocus,
  onIconClick,
  onKeyDown,
  paddingLeft = 34,
  paddingRight,
  placeholder = ' ',
  textarea,
  touched,
  type = 'text',
  value,
  ...rest
}) => {
  const [shouldValidate, setShouldValidate] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement>()
  const focusHandler: FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    setIsFocused(true)
    onFocus && onFocus(e)
  }

  const onKeyPress: KeyboardEventHandler = (e) => {
    if (e.key === 'Enter') {
      if (onEnterPress) {
        onEnterPress()
      }
    }
    if (rest.onKeyPress) {
      // @ts-ignore
      rest.onKeyPress(e)
    }
  }

  const blur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (
    e,
  ) => {
    setShouldValidate(true)
    setIsFocused(false)
    onBlur && onBlur(e)
  }

  const classNameHandler = cn(
    'position-relative',
    {
      focused: isFocused,
      invalid: (shouldValidate || touched) && !isValid,
    },
    className,
  )

  const combinedInputClassName = cn(
    { error: !!errorMessage, 'form-control': true },
    inputClassName,
  )
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const charactersRemaining = rest.maxLength
    ? rest.maxLength - (value?.length || 0)
    : 0
  return (
    <>
      <div data-test={`${name}-container`} className={classNameHandler}>
        {textarea ? (
          <>
            {label && (
              <label className='form-label' htmlFor={id}>
                {label}
              </label>
            )}
            <textarea
              name={name}
              placeholder={placeholder}
              {...rest}
              onChange={(e) => {
                const modifiedValue = stripNonEuropeanCharacters(e.target.value)
                e.target.value = modifiedValue
                rest.onChange?.(e)
              }}
              id={id}
              // @ts-ignore
              ref={ref}
              onFocus={focusHandler}
              onKeyDown={onKeyDown}
              onBlur={blur}
              value={value}
              className={combinedInputClassName}
            />
          </>
        ) : (
          <>
            {!!label && (
              <label className='form-label' htmlFor={id}>
                {label}
              </label>
            )}
            <input
              disabled={disabled}
              name={name}
              type={type === 'password' && showPassword ? '' : type}
              {...rest}
              onChange={(e) => {
                const modifiedValue = stripNonEuropeanCharacters(e.target.value)
                e.target.value = modifiedValue
                rest.onChange?.(e)
              }}
              id={id}
              onKeyPress={onKeyPress}
              // @ts-ignore
              ref={ref}
              onFocus={focusHandler}
              onKeyDown={onKeyDown}
              onBlur={blur}
              value={value}
              placeholder={placeholder}
              className={combinedInputClassName}
              style={{
                paddingLeft: iconLeft ? paddingLeft : undefined,
                paddingRight: icon ? 34 : paddingRight ? paddingRight : undefined,
              }}
            />
            {iconLeft && (
              <Icon
                data-test={`${rest['data-test']}-iconLeft`}
                style={{
                  bottom: 0,
                  color: iconColour || '#F7A400',
                  cursor: 'pointer',
                  left: iconLeftPosition,
                  margin: 'auto 0',
                  position: 'absolute',
                  top: 0,
                }}
                fill={iconColour}
                name={iconLeft}
              />
            )}
            {actionButton && (
              <div
                onClick={onActionButtonClick}
                className={classNames('action-button', {
                  'action-button--focused': isFocused && !!value,
                  'action-button--not-visible': !isVisibleActionButton,
                })}
              >
                {actionButton}
              </div>
            )}
            {icon && (
              <Icon
                data-test={`${rest['data-test']}-icon`}
                onClick={() => {
                  if (type === 'password') {
                    setShowPassword(!showPassword)
                  } else {
                    onIconClick ? onIconClick() : ref?.current?.focus()
                  }
                }}
                style={{
                  bottom: 0,
                  color: iconColour || '#F7A400',
                  cursor: 'pointer',
                  margin: 'auto 0',
                  position: 'absolute',
                  right: 10,
                  top: 0,
                }}
                fill={iconColour}
                name={icon}
              />
            )}
          </>
        )}
        {children && children}
      </div>
      {!!rest.maxLength && textarea && (
        <span className='text-small'>
          {Strings.formatString(
            charactersRemaining === 1
              ? `${Strings.characterRemaining}`
              : `${Strings.charactersRemaining}`,
            charactersRemaining,
          )}
        </span>
      )}
    </>
  )
}

Input.displayName = 'Input'
export default Input
